// ============================================================================
// 렌더 — three.js. 시뮬의 RS 를 보간해서 그린다. 조명 연출 없음, 중간 회색 배경.
// ============================================================================
import * as THREE from 'three';
import { P, WORLD, CARD_DRAW, CARD_COUNT, CardKind, REVEAL_TINT } from './params';
import { RS, Sim } from './sim';
import { CardMesh, PackPiece, SurfaceUniforms } from './geometry';
import { TexSet, makeMoneyTexture } from './assets';
import { BinderView } from './binder-render';
import { BinderRS } from './binder-sim';

/** REVEAL_TINT 에 없는 등급의 폴백 — 색 없는 흰빛. */
const WHITE_TINT: [number, number, number] = [1, 1, 1];

export class Renderer {
  gl: THREE.WebGLRenderer;
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  root = new THREE.Group();
  topGroup = new THREE.Group();
  bodyGroup = new THREE.Group();
  stackGroup = new THREE.Group();
  /** 바인더는 같은 scene 에 있고 화면에 따라 root.visible 만 갈린다. */
  binder: BinderView;
  topFront: PackPiece;
  topInner: PackPiece;
  botFront: PackPiece;
  botInner: PackPiece;
  cards: CardMesh[] = [];
  graphicsOn = true;
  /** 창 크기가 바뀌었을 때. main.ts 가 binderSim.aspect 를 따라가게 한다 (펼침 모드 판정). */
  onResize: ((aspect: number) => void) | null = null;
  width = 1;
  height = 1;
  aspect = 0.5;
  /** 떠오르는 금액 텍스트 (`-$4.49`). 팩과 같은 공간에 있는 쿼드다. */
  private moneyMesh!: THREE.Mesh;
  private moneyMat!: THREE.MeshBasicMaterial;
  private moneyKey = -1;
  private cardBent = new Uint8Array(CARD_COUNT);
  /** 지금 AR·UR 등장 연출이 걸린 카드 index. -1 = 없음. 끝날 때 되돌려야 해서 들고 있는다. */
  private fxIdx = -1;
  /** 이번 팩의 카드 id. 해제 보존 집합이자 진단용 목록이다 (null = 그 자리는 등급 그림). */
  private packIds: (string | null)[] = [];
  private packDirty = true;
  private builtScale = 0; // 카드 메시를 만들 때 쓴 world.objectScale
  private tmpQ = new THREE.Quaternion();
  private tmpAxis = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement, public sim: Sim, private tex: TexSet) {
    this.gl = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
    this.gl.setClearColor(0x7a7a7a, 1);
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.camera = new THREE.PerspectiveCamera(WORLD.cameraFov, 1, 0.5, 200);
    this.scene.add(this.root);
    this.root.add(this.topGroup, this.bodyGroup, this.stackGroup);
    this.binder = new BinderView(tex);
    this.scene.add(this.binder.root);
    // 개봉용 카드 그림이 늦게 도착하면 그 프레임 전에 다시 입힌다. BinderView 가 이미 건 콜백을
    // 지우지 않고 앞에 이어 붙인다 (바인더의 texVersion / onDetailTex 가 계속 살아 있어야 한다).
    const onArrive = this.binder.cardCache.onArrive;
    this.binder.cardCache.onArrive = (id, ok, tier) => {
      onArrive?.(id, ok, tier);
      if (tier === 'open') this.applyCardTextures();
    };

    const w = WORLD.packW, h = WORLD.packH;
    this.topFront = new PackPiece(w, h, true, 56, 8, tex.packFront, 0, 0.6, 0.3);
    this.topInner = new PackPiece(w, h, true, 56, 8, tex.packInner, -0.5, 0.05, 0.0);
    this.botFront = new PackPiece(w, h, false, 56, 60, tex.packFront, 0, 0.6, 0.3);
    this.botInner = new PackPiece(w, h, false, 56, 60, tex.packInner, -0.5, 0.05, 0.0);
    this.topGroup.add(this.topFront.mesh, this.topInner.mesh);
    this.bodyGroup.add(this.botFront.mesh, this.botInner.mesh);

    // 금액 텍스트. depthTest 를 끄고 renderOrder 를 올려 팩·카드 위에 항상 뜬다
    // (팩 조각이 z 로 튀어나가도 글자가 파묻히지 않는다).
    this.moneyMat = new THREE.MeshBasicMaterial({ transparent: true, depthTest: false, depthWrite: false, opacity: 0 });
    this.moneyMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.moneyMat);
    this.moneyMesh.renderOrder = 100;
    this.moneyMesh.visible = false;
    this.root.add(this.moneyMesh);

    this.rebuildPack();
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /** 카드 메시를 현재 크기로 다시 만든다. 크기(world.objectScale)가 바뀔 때만 호출된다. */
  private rebuildCards(): void {
    for (const c of this.cards) {
      this.stackGroup.remove(c.mesh);
      c.geo.dispose();
    }
    this.cards.length = 0;
    for (let k = 0; k < CARD_COUNT; k++) {
      const c = new CardMesh(WORLD.cardW, WORLD.cardH, WORLD.cardThickness, this.tex.cardFronts[CARD_DRAW.kinds[k]], this.tex.cardBack);
      this.cards.push(c);
      this.stackGroup.add(c.mesh);
    }
    this.cardBent.fill(0);
    // 메시를 새로 만들었으니 등장 연출이 걸려 있던 카드도 사라졌다.
    this.fxIdx = -1;
    this.builtScale = P.world.objectScale;
  }

  /**
   * 뽑기 결과대로 앞면을 다시 입힌다. 리셋(새 뽑기)·크기 변경·텍스처가 늦게 도착했을 때 전부 여기로 온다.
   *
   * **카드별 그림이 정본이고 등급 그림은 폴백이다** (BINDER_DESIGN §7.5). `CARD_DRAW.defIds[k]` 의
   * 그림을 캐시에 요청하고, 아직 안 왔거나(비동기) 파일이 없거나(404) id 자체가 null 이면
   * (그 등급에 레지스트리 카드가 0장 — 지금 UR 이 그렇다) `cardFronts` 의 등급 그림을 쓴다.
   * **로딩 화면은 없다** — 등급 그림으로 시작해서 도착하는 대로 갈아끼운다 (setTexture 와 같은 패턴).
   *
   * 캐시는 바인더 것을 그대로 쓴다 — 같은 파일이라 바인더에서 이미 받아 둔 카드면 blob 을 다시 안 받는다.
   */
  private applyCardTextures(): void {
    const cache = this.binder.cardCache;
    const w = P.open.cardTexWidth;
    const keep = new Set<string>();
    this.packIds.length = 0;
    for (let k = 0; k < this.cards.length; k++) {
      const id = CARD_DRAW.defIds[k] ?? null;
      let t: THREE.Texture | null = null;
      if (id) {
        keep.add(id);
        t = cache.requestOpen(id, w);
      }
      this.packIds.push(t ? id : null);
      this.cards[k].setFrontTexture(t ?? this.tex.cardFronts[CARD_DRAW.kinds[k]]);
    }
    // 직전 팩 해제. **새 팩을 요청한 뒤**라서 같은 카드가 또 나왔으면 파일을 다시 안 받는다.
    cache.releaseOpen(keep);
  }

  /** 진단·검증용 — 각 자리에 실제 카드 그림이 붙었으면 그 id (등급 폴백이면 null). */
  packArtIds(): (string | null)[] {
    return this.packIds.slice();
  }

  /** 절단선 프로파일이 바뀌면(리셋) 호출. 크기·뽑기 결과가 바뀌었으면 여기서 같이 반영된다. */
  rebuildPack(): void {
    if (this.builtScale !== P.world.objectScale) {
      for (const p of [this.topFront, this.topInner, this.botFront, this.botInner]) {
        p.w = WORLD.packW;
        p.h = WORLD.packH;
      }
      this.rebuildCards();
    }
    this.applyCardTextures();
    const jag = this.sim.jagY;
    const py = this.sim.perfY;
    this.topFront.build(jag, py);
    this.topInner.build(jag, py);
    this.botFront.build(jag, py);
    this.botInner.build(jag, py);
    this.packDirty = true;
  }

  setTexture(name: string, tex: THREE.Texture): void {
    const setMap = (p: PackPiece) => ((p.material.uniforms as unknown as SurfaceUniforms).map.value = tex);
    // 크기를 바꾸면 카드 메시를 다시 만들기 때문에, 받은 텍스처를 tex 에도 기록해 둬야 한다
    if (name === 'pack_front') {
      this.tex.packFront = tex;
      setMap(this.topFront);
      setMap(this.botFront);
    } else if (name === 'pack_inner') {
      this.tex.packInner = tex;
      setMap(this.topInner);
      setMap(this.botInner);
    } else if (name === 'card_back') {
      this.tex.cardBack = tex;
      for (const c of this.cards) c.setBackTexture(tex);
    } else if (name in this.tex.cardFronts) {
      this.tex.cardFronts[name as CardKind] = tex;
      this.applyCardTextures();
    } else {
      this.binder.setTexture(name, tex);
    }
  }

  resize(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.aspect = this.width / Math.max(1, this.height);
    this.camera.aspect = this.aspect;
    const halfH = WORLD.viewH / 2;
    this.camera.position.set(0, 0, halfH / Math.tan((WORLD.cameraFov * Math.PI) / 360));
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.gl.setSize(this.width, this.height, false);
    this.sim.aspect = this.aspect;
    this.onResize?.(this.aspect);
  }

  toWorld = (cx: number, cy: number): { x: number; y: number } => {
    return {
      x: (cx / this.width - 0.5) * WORLD.viewH * this.aspect,
      y: (0.5 - cy / this.height) * WORLD.viewH,
    };
  };

  toScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: (x / (WORLD.viewH * this.aspect) + 0.5) * this.width,
      y: (0.5 - y / WORLD.viewH) * this.height,
    };
  }

  /**
   * 화면이 둘이라 시그니처만 넓혔다. `r` 이 있으면 개봉, `b` 가 있으면 바인더.
   * 개봉 화면 부분은 한 줄도 안 바뀌었다 (§10 조건 4).
   */
  draw(r: RS | null, b: BinderRS | null = null): void {
    if (!this.graphicsOn) {
      this.root.visible = false;
      this.binder.root.visible = false;
      this.gl.render(this.scene, this.camera);
      return;
    }
    if (b) {
      this.root.visible = false;
      this.binder.draw(b, this.aspect);
      this.gl.render(this.scene, this.camera);
      return;
    }
    this.binder.root.visible = false;
    if (!r) {
      this.gl.render(this.scene, this.camera);
      return;
    }
    this.root.visible = true;

    // ---- 팩
    this.topGroup.visible = r.topVis > 0.5;
    this.topGroup.position.set(r.topX, this.topFront.centerY + r.topY, 0);
    this.topGroup.rotation.z = r.topRot;
    this.bodyGroup.position.y = r.bodyY;

    if (r.cutDir !== 0 && r.cutFront >= 0) {
      const dir = r.cutDir, edge = r.cutStartEdgeX, front = r.cutFront, startD = r.cutStartD;
      const gapHalf = 0.5 * P.cut.gapAmount * r.gapScale;
      const ramp = Math.max(0.05, P.cut.gapRamp);
      const gapAt = (x: number): number => {
        const d = (x - edge) * dir;
        if (d < startD) return 0;
        const a = Math.max(0, Math.min(1, (front - d) / ramp));
        const b = Math.max(0, Math.min(1, (d - startD) / 0.35));
        return gapHalf * a * b;
      };
      const bR = Math.max(0.05, P.cut.bulgeRadius);
      const bulge = (x: number, dist: number, out: Float64Array) => {
        const d = (x - edge) * dir;
        const wx = Math.exp(-((d - r.bulgeD) * (d - r.bulgeD)) / (bR * bR));
        const wy = Math.exp(-(dist * dist) / (bR * bR));
        const a = r.bulgeAmt * wx * wy;
        out[0] = a * 0.5;
        out[1] = a * 2.0;
      };
      const falloff = 1.0;
      this.topFront.applyCut((x) => gapAt(x), falloff, bulge);
      this.botFront.applyCut((x) => -gapAt(x), falloff, bulge);
      // 내부면은 틈 변형 없이 융기만 (뒤에서 보이는 면)
      const noGap = () => 0;
      const noBulge = (_x: number, _d: number, out: Float64Array) => {
        out[0] = 0;
        out[1] = 0;
      };
      this.topInner.applyCut(noGap, falloff, noBulge);
      this.botInner.applyCut(noGap, falloff, noBulge);
      this.packDirty = true;
      const u = this.topFront.material.uniforms as unknown as SurfaceUniforms;
      u.gloss.value = 0.6 + 0.5 * r.tension;
    } else if (this.packDirty) {
      this.topFront.resetFlat();
      this.botFront.resetFlat();
      this.topInner.resetFlat();
      this.botInner.resetFlat();
      (this.topFront.material.uniforms as unknown as SurfaceUniforms).gloss.value = 0.6;
      this.packDirty = false;
    }

    // ---- 스택
    this.stackGroup.position.set(r.stackX, r.stackY, r.stackZ);
    if (r.flipMode < 0.5) {
      this.tmpAxis.set(Math.cos(r.flipTilt), Math.sin(r.flipTilt), 0);
      this.tmpQ.setFromAxisAngle(this.tmpAxis, r.flipTheta);
      this.stackGroup.quaternion.copy(this.tmpQ);
    } else {
      this.stackGroup.quaternion.setFromAxisAngle(this.tmpAxis.set(0, 0, 1), r.roll);
    }
    const flipped = r.flipMode >= 0.5;
    for (let k = 0; k < this.cards.length; k++) {
      const c = this.cards[k];
      const vis = r.cvis[k] > 0.5;
      c.mesh.visible = vis;
      if (!vis) continue;
      c.mesh.position.set(r.cx[k], r.cy[k], r.cz[k]);
      c.mesh.rotation.set(flipped ? Math.PI : 0, 0, r.crot[k]);
      const b = r.cbend[k];
      if (b > 1e-4) {
        // 휘어짐 축을 world → 카드 로컬로. 메시는 Z(crot) 뒤에 X(π, 뒤집힘) 순서로 회전한다.
        const rot = r.crot[k];
        const cr = Math.cos(rot), sr = Math.sin(rot);
        const ldx = r.cbdx[k] * cr + r.cbdy[k] * sr;
        let ldy = -r.cbdx[k] * sr + r.cbdy[k] * cr;
        if (flipped) ldy = -ldy;
        c.applyBend(b, r.ccontact[k], ldx, ldy, flipped ? -1 : 1);
        this.cardBent[k] = 1;
      } else if (this.cardBent[k]) {
        c.resetFlat();
        this.cardBent[k] = 0;
      }
    }

    this.drawRevealFx(r);
    this.drawMoney(r);

    this.gl.render(this.scene, this.camera);
  }

  /**
   * AR·UR 등장 연출. **밴드 위치·세기는 sim 이 계산해 RS 로 보낸 값**이고 여기서는 셰이더에 꽂기만 한다.
   * 건드리는 것은 **그 카드 앞면 머티리얼의 uniform 뿐** — 위치·크기·지오메트리는 안 만진다.
   * 그래서 연출 중에도 그 카드를 잡는 드래그가 그대로 먹는다 (P.reveal 주석의 규칙 1).
   */
  private drawRevealFx(r: RS): void {
    const idx = r.fxIdx | 0;
    if (this.fxIdx !== idx) {
      // 이전 카드의 uniform 을 되돌린다. 안 되돌리면 그 카드에 빛이 박힌 채로 남는다.
      if (this.fxIdx >= 0 && this.fxIdx < this.cards.length) this.cards[this.fxIdx].clearFrontFx();
      this.fxIdx = idx;
    }
    if (idx < 0 || idx >= this.cards.length) return;
    const tint = REVEAL_TINT[r.fxRank | 0] ?? WHITE_TINT;
    this.cards[idx].setFrontFx(r.fxBand, r.fxWidth, r.fxStrength, r.fxFlash, tint);
  }

  /**
   * 떠오르는 금액 텍스트. **위치·투명도는 sim 이 계산해 RS 로 보낸 값**이고
   * 여기서는 그리기만 한다 (CSS transition 도, 렌더러 자체 타이머도 없다).
   * 글자는 key 가 바뀔 때만 캔버스로 다시 굽는다.
   */
  private drawMoney(r: RS): void {
    if (r.floatKey <= 0 || r.floatA <= 0.002) {
      this.moneyMesh.visible = false;
      return;
    }
    if (this.moneyKey !== r.floatKey) {
      this.moneyKey = r.floatKey;
      const f = this.sim.float;
      this.moneyMat.map?.dispose();
      this.moneyMat.map = makeMoneyTexture(f.text, f.sign >= 0 ? '#8ee089' : '#ff6f5c');
      this.moneyMat.needsUpdate = true;
    }
    const img = this.moneyMat.map?.image as { width: number; height: number } | undefined;
    const h = P.econ.floatSize * 2; // 캔버스 높이는 글자 크기의 2배 (여백 포함)
    const w = img ? (h * img.width) / img.height : h;
    this.moneyMesh.visible = true;
    this.moneyMesh.scale.set(w, h, 1);
    this.moneyMesh.position.set(r.floatX, r.floatY + r.floatDY, 0);
    this.moneyMat.opacity = r.floatA;
  }
}
